import { useEffect, useState } from "react";
import { Outlet, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "../stores/authStore";
import { useCartStore } from "../stores/cartStore";
import { getMenuUrl, useRestaurantId } from "../utils/restaurant";
import { checkAnalyticsAccess } from "../utils/analyticsAccess";
import {
  AppShell,
  Burger,
  Group,
  ActionIcon,
  Anchor,
  Text,
  Button,
  Indicator,
  Stack,
  useComputedColorScheme,
  useMantineColorScheme,
  Container,
  // Divider,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconShoppingCart,
  IconMoon,
  IconSun,
  IconLogout,
} from "@tabler/icons-react";
// import LanguageSwitcher from "./LanguageSwitcher";
import logo from "../assets/orange_v4.png";
import BreadcrumbsBar from "./BreadcrumbsBar";

export default function Layout() {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const initializeAuth = useAuthStore((state) => state.initializeAuth);
  const items = useCartStore((state) => state.items);
  const navigate = useNavigate();
  const [opened, { toggle, close }] = useDisclosure();
  const { setColorScheme } = useMantineColorScheme();
  const computedColorScheme = useComputedColorScheme("light", {
    getInitialValueInEffect: true,
  });
  const [hasAnalyticsAccess, setHasAnalyticsAccess] = useState(false);

  // Get restaurant ID (La Vida)
  const restaurantId = useRestaurantId();

  // Initialize auth on mount - fetch user data if token exists
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Check analytics access when user changes
  useEffect(() => {
    if (isAuthenticated && user) {
      checkAnalyticsAccess().then(setHasAnalyticsAccess);
    } else {
      setHasAnalyticsAccess(false);
    }
  }, [isAuthenticated, user]);

  const handleLogout = () => {
    logout();
    navigate("/");
    close();
  };

  const cartItemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <AppShell
      header={{ height: 64 }}
      navbar={{
        width: 280,
        breakpoint: "sm",
        collapsed: { mobile: !opened, desktop: true },
      }}
      footer={{ height: 60 }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="sm"
              size="sm"
              aria-label="menu"
              // hidden={true}
            />
            {/* Disable the burger menu on mobile for now */}
            {/* Menu link on desktop - left side */}
            <Group gap="sm" visibleFrom="sm">
              <Anchor
                component={Link}
                to={getMenuUrl(restaurantId)}
                underline="hover"
                style={{ fontWeight: 500, fontSize: "0.9375rem" }}
              >
                {t("nav.menu")}
              </Anchor>
              <Anchor
                component={Link}
                to="/order-history"
                underline="hover"
                style={{ fontWeight: 500, fontSize: "0.9375rem" }}
              >
                {t("nav.orderHistory")}
              </Anchor>
            </Group>
          </Group>

          {/* Mobile: Centered logo */}
          <Anchor
            component={Link}
            to="/"
            underline="never"
            onClick={close}
            hiddenFrom="sm"
            style={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "center",
            }}
          >
            <img src={logo} alt="Smagly Logo" style={{ height: 99 }} />
          </Anchor>

          {/* Desktop: Centered logo */}
          <Anchor
            component={Link}
            to="/"
            underline="never"
            onClick={close}
            visibleFrom="sm"
            style={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "center",
            }}
          >
            {/* Logo image - desktop */}
            <img src={logo} alt="Smagly Logo" style={{ height: 70 }} />
          </Anchor>

          {/* Right side actions */}
          <Group gap="xs">
            {/* Desktop navigation - moved to right side */}
            {isAuthenticated && (
              <Group gap="sm" visibleFrom="sm">
                {/* Orders page hidden for now - only accessible to admin@smagly.com via direct URL */}
                {/* <Anchor component={Link} to="/orders" underline="hover">
                  {t("nav.orders")}
                </Anchor> */}
                {(user?.role === "admin" || user?.role === "staff") && (
                  <Anchor
                    component={Link}
                    to="/restaurant"
                    underline="hover"
                    data-testid="nav-restaurant-link-desktop"
                    style={{ fontWeight: 500, fontSize: "0.9375rem" }}
                  >
                    Restaurant
                  </Anchor>
                )}
                {hasAnalyticsAccess && (
                  <Anchor
                    component={Link}
                    to="/analytics"
                    underline="hover"
                    data-testid="nav-analytics-link-desktop"
                    style={{ fontWeight: 500, fontSize: "0.9375rem" }}
                  >
                    Analytics
                  </Anchor>
                )}
              </Group>
            )}
            {/* <LanguageSwitcher /> */}

            <ActionIcon
              onClick={() =>
                setColorScheme(
                  computedColorScheme === "light" ? "dark" : "light"
                )
              }
              variant="subtle"
              aria-label="Toggle color scheme"
              style={{ display: "none" }} // hide for now
            >
              {computedColorScheme === "light" ? (
                <IconMoon size={20} />
              ) : (
                <IconSun size={20} />
              )}
            </ActionIcon>

            <Indicator
              label={cartItemCount || null}
              size={16}
              disabled={cartItemCount === 0}
            >
              <ActionIcon
                component={Link}
                to="/cart"
                variant="filled"
                color="brand"
                size="lg"
                aria-label="cart"
              >
                <IconShoppingCart size={20} />
              </ActionIcon>
            </Indicator>

            {isAuthenticated ? (
              <Group gap="xs" visibleFrom="sm">
                <Text size="sm" style={{ fontWeight: 500 }}>
                  {t("nav.hi")}, {user?.name}
                </Text>
                <Button
                  variant="subtle"
                  leftSection={<IconLogout size={16} />}
                  onClick={handleLogout}
                  size="sm"
                  style={{ fontWeight: 500 }}
                >
                  {t("nav.logout")}
                </Button>
              </Group>
            ) : (
              <Group gap="xs" visibleFrom="sm">
                <Button
                  component={Link}
                  to="/login"
                  variant="subtle"
                  size="sm"
                  style={{ fontWeight: 500 }}
                >
                  {t("nav.login")}
                </Button>
                <Button
                  component={Link}
                  to="/register"
                  color="brand"
                  size="sm"
                  style={{ fontWeight: 500 }}
                >
                  {t("nav.register")}
                </Button>
              </Group>
            )}
          </Group>
        </Group>
      </AppShell.Header>

      {/* Mobile navigation */}
      <AppShell.Navbar
        p="md"
        style={{
          height: "100vh",
        }}
      >
        <Stack gap="md">
          <Anchor
            component={Link}
            to={getMenuUrl(restaurantId)}
            onClick={close}
            underline="hover"
            data-testid="nav-menu-link"
            style={{ fontWeight: 500, fontSize: "1rem" }}
          >
            {t("nav.menu")}
          </Anchor>
          <Anchor
            component={Link}
            to="/order-history"
            onClick={close}
            underline="hover"
            data-testid="nav-order-history-link"
            style={{ fontWeight: 500, fontSize: "1rem" }}
          >
            {t("nav.orderHistory")}
          </Anchor>
          {isAuthenticated ? (
            <>
              {/* Orders page hidden for now - only accessible to admin@smagly.com via direct URL */}
              {/* <Anchor
                component={Link}
                to="/orders"
                onClick={close}
                underline="hover"
              >
                {t("nav.orders")}
              </Anchor> */}
              {(user?.role === "admin" || user?.role === "staff") && (
                <Anchor
                  component={Link}
                  to="/restaurant"
                  onClick={close}
                  underline="hover"
                  data-testid="nav-restaurant-link-mobile"
                  style={{ fontWeight: 500, fontSize: "1rem" }}
                >
                  Restaurant
                </Anchor>
              )}
              {hasAnalyticsAccess && (
                <Anchor
                  component={Link}
                  to="/analytics"
                  onClick={close}
                  underline="hover"
                  data-testid="nav-analytics-link-mobile"
                  style={{ fontWeight: 500, fontSize: "1rem" }}
                >
                  Analytics
                </Anchor>
              )}
              <Text size="sm" c="dimmed" style={{ fontWeight: 500 }}>
                {t("nav.hi")}, {user?.name}
              </Text>
              <Button
                variant="light"
                leftSection={<IconLogout size={16} />}
                onClick={handleLogout}
                fullWidth
                style={{ fontWeight: 500 }}
              >
                {t("nav.logout")}
              </Button>
            </>
          ) : (
            <>
              <Button
                component={Link}
                to="/login"
                variant="light"
                onClick={close}
                fullWidth
                style={{ fontWeight: 500 }}
              >
                {t("nav.login")}
              </Button>
              <Button
                component={Link}
                to="/register"
                color="brand"
                onClick={close}
                fullWidth
                style={{ fontWeight: 500 }}
              >
                {t("nav.register")}
              </Button>
            </>
          )}
        </Stack>
      </AppShell.Navbar>

      <AppShell.Main>
        <BreadcrumbsBar />
        <Outlet />
      </AppShell.Main>

      <AppShell.Footer style={{ position: "static" }}>
        <Container size="xl" h="100%">
          <Group h="100%" justify="space-between" px="md">
            <Text size="sm" c="dimmed">
              {/* © 2024 Smagly ApS · CVR: 12345678 */}© 2025 Smagly
            </Text>
            <Group gap="md">
              <Anchor
                component={Link}
                to="/contact"
                size="sm"
                c="dimmed"
                underline="hover"
              >
                {t("nav.contact")}
              </Anchor>
            </Group>
          </Group>
        </Container>
      </AppShell.Footer>
    </AppShell>
  );
}
